import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { toast } from "sonner";
import { API } from "../lib/api";
import { Download, Printer, QrCode, FileDown, CreditCard } from "lucide-react";

const SITE_URL = "https://www.bentskingo.com";
const FW = 1240, FH = 1754;

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

const loadImg = (src) =>
  new Promise((res, rej) => {
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => res(im);
    im.onerror = rej;
    im.src = src;
  });

function drawPassenger(ctx, logo, qr) {
  const W = FW, H = FH;
  const g = ctx.createLinearGradient(0, 0, W, H);
  g.addColorStop(0, "#FFC531"); g.addColorStop(0.45, "#F26419"); g.addColorStop(1, "#E5447B");
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  ctx.textAlign = "center";
  if (logo) ctx.drawImage(logo, W / 2 - 95, 88, 190, 190);
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "800 78px 'Poppins','Montserrat',system-ui,sans-serif";
  ctx.fillText("Mboa Go", W / 2, 358);
  ctx.font = "700 44px 'Poppins','Montserrat',system-ui,sans-serif";
  ctx.fillStyle = "#FFF7E8";
  ctx.fillText("Vos trajets en toute confiance", W / 2, 424);
  ctx.fillStyle = "#FFD23F";
  roundRect(ctx, 80, 468, W - 160, 150, 28); ctx.fill();
  ctx.fillStyle = "#1A1A1A";
  ctx.font = "800 44px 'Poppins','Montserrat',system-ui,sans-serif";
  ctx.fillText("3 MOIS DE COURSES GRATUITES", W / 2, 530);
  ctx.font = "700 36px 'Poppins','Montserrat',system-ui,sans-serif";
  ctx.fillText("Profitez-en lors du lancement !", W / 2, 580);
  const cardW = 760, cardH = 740, cardX = (W - cardW) / 2, cardY = 680;
  ctx.fillStyle = "#FFFFFF"; roundRect(ctx, cardX, cardY, cardW, cardH, 48); ctx.fill();
  ctx.drawImage(qr, W / 2 - 310, cardY + 60, 620, 620);
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "800 52px 'Poppins','Montserrat',system-ui,sans-serif";
  ctx.fillText("Scannez pour commander", W / 2, cardY + cardH + 88);
  ctx.fillText("votre course !", W / 2, cardY + cardH + 150);
  ctx.fillStyle = "#1A1A1A";
  roundRect(ctx, (W - 560) / 2, cardY + cardH + 210, 560, 92, 46); ctx.fill();
  ctx.fillStyle = "#FFD23F";
  ctx.font = "800 46px 'Poppins','Montserrat',system-ui,sans-serif";
  ctx.fillText("www.bentskingo.com", W / 2, cardY + cardH + 266);
}

export function PromoPoster({ agentCode }) {
  const canvasRef = useRef(null);
  const [png, setPng] = useState("");
  const q = agentCode ? `?agent=${encodeURIComponent(agentCode)}` : "";
  const paxUrl = agentCode ? `${SITE_URL}/auth?role=passenger&agent=${agentCode}` : SITE_URL;

  useEffect(() => {
    let cancelled = false;
    const build = async () => {
      const canvas = canvasRef.current || document.createElement("canvas");
      canvas.width = FW; canvas.height = FH;
      const ctx = canvas.getContext("2d");
      const qrUrl = await QRCode.toDataURL(paxUrl, { width: 760, margin: 1, errorCorrectionLevel: "H", color: { dark: "#1A1A1A", light: "#FFFFFF" } });
      const [logo, qr] = await Promise.all([loadImg("/brand-logo.png").catch(() => null), loadImg(qrUrl)]);
      try { if (document.fonts) await document.fonts.ready; } catch { /* ignore */ }
      drawPassenger(ctx, logo, qr);
      if (!cancelled) setPng(canvas.toDataURL("image/png"));
    };
    build().catch(() => {});
    return () => { cancelled = true; };
  }, [paxUrl]);

  const dl = (url, name) => { const a = document.createElement("a"); a.href = url; a.download = name; a.click(); };
  const downloadFlyer = () => { dl(`${API}/marketing/flyer.pdf${q}`, "bentskin-go-flyer-A5-rectoverso.pdf"); toast.success("Flyer A5 recto-verso (CMJN 300 DPI) en téléchargement ✅"); };
  const downloadCard = () => { dl(`${API}/marketing/card.pdf${q}`, "bentskin-go-carte-visite.pdf"); toast.success("Carte de visite recto-verso (CMJN 300 DPI) en téléchargement ✅"); };
  const downloadPng = () => { if (png) dl(png, "bentskin-go-flyer-passager.png"); };
  const printPoster = () => {
    if (!png) return;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Autorisez les pop-ups pour imprimer."); return; }
    w.document.write(`<html><head><title>Flyer Mboa Go</title></head><body style="margin:0;display:flex;justify-content:center;background:#fff"><img src="${png}" style="max-width:100%;height:auto"/><script>window.onload=function(){setTimeout(function(){window.print()},250)}<\/script></body></html>`);
    w.document.close();
  };

  return (
    <div data-testid="promo-poster-card" className="bg-white rounded-2xl border border-[#E2DFD8] p-6 print:hidden">
      <h2 className="font-head font-bold text-lg text-[#1A1A1A] flex items-center gap-2">
        <QrCode className="w-5 h-5 text-[#D94F2B]" /> Supports marketing — Flyer &amp; Carte de visite
      </h2>
      <p className="mt-1 text-sm text-[#75807A]">
        Flyer A5 recto-verso (Chauffeurs / Passagers) et carte de visite, prêts pour Vistaprint (CMJN, 300 DPI, fonds perdus).
        {agentCode ? <span className="block mt-1 font-semibold text-[#D94F2B]">QR de parrainage personnalisé · Agent {agentCode}</span>
          : <span className="block mt-1">QR générique (sans code agent).</span>}
      </p>
      <div className="mt-5 flex flex-col sm:flex-row gap-6 items-center sm:items-start">
        <div className="shrink-0 rounded-2xl overflow-hidden shadow-[0_12px_40px_rgba(0,0,0,0.12)] border border-[#E2DFD8] bg-[#FDF8F0]">
          {png ? (
            <img data-testid="promo-poster-img" src={png} alt="Flyer Mboa Go" className="w-[240px] sm:w-[280px] h-auto block" />
          ) : (
            <div className="w-[240px] sm:w-[280px] aspect-[1240/1754] animate-pulse bg-[#E2DFD8]" />
          )}
          <div className="text-[11px] text-[#75807A] text-center py-1.5 px-2">Aperçu · côté Passager (le PDF contient aussi le côté Chauffeur)</div>
        </div>
        <div className="flex-1 w-full space-y-3">
          <button data-testid="promo-download-flyer" onClick={downloadFlyer} className="w-full h-12 px-5 rounded-xl bg-gradient-to-r from-[#F26419] to-[#E5447B] hover:opacity-90 text-white font-bold inline-flex items-center justify-center gap-2 transition-opacity">
            <FileDown className="w-4 h-4" /> Flyer A5 recto-verso · Chauffeurs / Passagers
          </button>
          <button data-testid="promo-download-card" onClick={downloadCard} className="w-full h-12 px-5 rounded-xl bg-[#1A1A1A] hover:bg-black text-white font-bold inline-flex items-center justify-center gap-2 transition-colors">
            <CreditCard className="w-4 h-4" /> Carte de visite recto-verso (85×55 mm)
          </button>
          <button data-testid="promo-download" onClick={downloadPng} disabled={!png} className="w-full h-12 px-5 rounded-xl bg-white border border-[#E2DFD8] hover:border-[#D94F2B] disabled:opacity-50 text-[#1A1A1A] font-bold inline-flex items-center justify-center gap-2 transition-colors">
            <Download className="w-4 h-4" /> Image PNG (aperçu passager)
          </button>
          <button data-testid="promo-print" onClick={printPoster} disabled={!png} className="w-full h-12 px-5 rounded-xl bg-white border border-[#E2DFD8] hover:border-[#D94F2B] disabled:opacity-50 text-[#1A1A1A] font-bold inline-flex items-center justify-center gap-2 transition-colors">
            <Printer className="w-4 h-4" /> Imprimer l'aperçu
          </button>
        </div>
      </div>
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
